import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ArrowBack, DeleteOutline, DownloadOutlined, EditOutlined, ExpandMore } from "@mui/icons-material";
import Header from "../../components/Header";
import MarkdownView from "../../components/MarkdownView";
import EntryDialog from "./EntryDialog";
import {
  useGetEntriesQuery,
  useGetEntryQuery,
  useUpdateEntryMutation,
  useDeleteEntryMutation,
} from "../../services/cookbook.service";
import { canEditCookbook, formatDate, slugify, stripLeadingTitle } from "../../common/cookbook";

const CookbookEntry = () => {
  const { user: currentUser } = useSelector((state) => state.auth);

  if (!currentUser) {
    return (
      <Box m="1.5rem 2.5rem">
        <Header title="Cookbook" />
        <Typography sx={{ mt: "1.5rem" }}>Log in to view the cookbook.</Typography>
      </Box>
    );
  }
  return <EntryPage canEdit={canEditCookbook(currentUser)} />;
};

const SectionLinks = ({ sections }) =>
  sections.map((section) => (
    <Link
      key={section.id}
      component="button"
      underline="none"
      color="text.secondary"
      onClick={() => document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        fontSize: "0.85rem",
        lineHeight: 1.4,
        py: "0.35rem",
        "&:hover": { color: "secondary.main" },
      }}
    >
      {section.text}
    </Link>
  ));

const EntryPage = ({ canEdit }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { entryId } = useParams();
  const wide = useMediaQuery(theme.breakpoints.up("lg"));
  // below this width the layout's floating menu button sits over the top-left corner
  const isNonMobile = useMediaQuery("(min-width: 890px)");

  const { data: entry, isLoading, isError, error } = useGetEntryQuery(entryId);
  const { data: entries } = useGetEntriesQuery("");
  const [updateEntry, { isLoading: saving }] = useUpdateEntryMutation();
  const [deleteEntry] = useDeleteEntryMutation();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notice, setNotice] = useState(null);
  const [sections, setSections] = useState([]);
  const contentRef = useRef(null);

  const body = useMemo(() => (entry ? stripLeadingTitle(entry.content) : ""), [entry]);

  // Table of contents from the rendered "##" headings (MarkdownView gives them ids).
  useEffect(() => {
    if (!contentRef.current) {
      setSections([]);
      return;
    }
    setSections(
      [...contentRef.current.querySelectorAll("h2[id]")].map((heading) => ({
        id: heading.id,
        text: heading.textContent,
      }))
    );
  }, [body]);

  const categories = [...new Set((entries || []).map((item) => item.category).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );

  const handleDownload = () => {
    const blob = new Blob([entry.content + "\n"], { type: "text/markdown;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slugify(entry.title)}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleSave = (values) =>
    updateEntry({ id: entry._id, ...values })
      .unwrap()
      .then(() => setNotice({ severity: "success", text: "Topic saved." }));

  const handleDelete = () => {
    setConfirmDelete(false);
    deleteEntry(entry._id)
      .unwrap()
      .then(() => navigate("/cookbook"))
      .catch((err) => setNotice({ severity: "error", text: err?.data?.message || "Delete failed." }));
  };

  const showContents = sections.length > 1;

  return (
    <Box m="1.5rem 2.5rem">
      <Button
        size="small"
        color="inherit"
        startIcon={<ArrowBack />}
        onClick={() => navigate("/cookbook")}
        sx={{ ml: isNonMobile ? "-0.75rem" : "2.5rem", mb: "1rem", color: "text.secondary" }}
      >
        Cookbook
      </Button>

      {isLoading && <CircularProgress sx={{ display: "block" }} />}
      {isError && (
        <Typography color="error">
          {error?.status === 404 ? "This topic is not in the cookbook." : "Could not load this topic."}
        </Typography>
      )}

      {entry && (
        <>
          <Box display="flex" flexWrap="wrap" alignItems="flex-end" justifyContent="space-between" gap="1rem">
            <Header title={entry.title} subtitle={entry.category} />
            <Box display="flex" gap="0.25rem">
              <Tooltip title="Download .md">
                <IconButton onClick={handleDownload}>
                  <DownloadOutlined />
                </IconButton>
              </Tooltip>
              {canEdit && (
                <>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => setEditOpen(true)}>
                      <EditOutlined />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => setConfirmDelete(true)}>
                      <DeleteOutline />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" mt="0.75rem">
            Updated {formatDate(entry.updatedAt)}
            {entry.updatedBy ? ` by ${entry.updatedBy.username}` : ""} · AI-generated: check doses and
            recommendations against current guidelines and local protocols.
          </Typography>

          <Box display="flex" alignItems="flex-start" gap="1.5rem" mt="1.5rem">
            <Paper
              sx={{
                flex: 1,
                minWidth: 0,
                p: { xs: "1.25rem", sm: "1.75rem 2rem" },
                backgroundColor: theme.palette.background.alt,
              }}
            >
              {!wide && showContents && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    mb: "1.25rem",
                    backgroundColor: "transparent",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "10px !important",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography fontWeight={600}>Contents</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <SectionLinks sections={sections} />
                  </AccordionDetails>
                </Accordion>
              )}
              <Box ref={contentRef}>
                <MarkdownView>{body}</MarkdownView>
              </Box>
            </Paper>

            {wide && showContents && (
              <Box
                component="nav"
                sx={{
                  width: 230,
                  flexShrink: 0,
                  position: "sticky",
                  top: "1.5rem",
                  maxHeight: "calc(100vh - 3rem)",
                  overflowY: "auto",
                }}
              >
                <Typography
                  fontSize="0.75rem"
                  fontWeight={700}
                  letterSpacing="0.06em"
                  textTransform="uppercase"
                  color="text.secondary"
                  mb="0.5rem"
                >
                  Contents
                </Typography>
                <SectionLinks sections={sections} />
              </Box>
            )}
          </Box>

          <EntryDialog
            open={editOpen}
            entry={entry}
            categories={categories}
            onClose={() => setEditOpen(false)}
            onSave={handleSave}
            saving={saving}
          />
          <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
            <DialogTitle>Delete topic?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Delete “{entry.title}” from the cookbook? This cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
              <Button color="error" variant="contained" onClick={handleDelete}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {notice ? (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} variant="filled">
            {notice.text}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Box>
  );
};

export default CookbookEntry;
